
import { Component, Mixins } from "vue-property-decorator";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import { namespace } from "vuex-class";
import vselect3 from "vselect3";
import { debounce } from "lodash";

import AlarmActionsMixin from "@/mixins/AlarmActionsMixin";
import { DeviceDetails, DeviceType, GetDeviceForGroupByTypeParams, GetDeviceParams } from "@/store/devices/types";
import { Response } from "@/store/responses/types";

const Devices = namespace("devices");
const Responses = namespace("responses");

const debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;

@Component({
	components: {
		"v-select-3": vselect3
	},
	validations: {
		currentAlarmAction: { objectID: { required }, settingsMeta: { required } },
		hasDataChanged: {
			required: requiredIf(function() {
				return (
					this.currentAlarmAction.settingsMeta === this.originalSettingsMeta &&
					this.currentAlarmAction.objectID === this.originalObjectId
				);
			})
		}
	}
})
export default class ActionSetupActivateRelay extends Mixins(AlarmActionsMixin, validationMixin) {
	@Devices.Action private fetchDevicesByType: (params: GetDeviceForGroupByTypeParams) => Promise<void>;
	@Devices.Mutation("resetState") private resetDevicesState: () => void;
	@Devices.State private devices: DeviceDetails[];
	@Responses.State private currentAlarm: Response;

	private originalObjectId: number = null;
	private originalSettingsMeta: string = null;
	private selectedDevice: DeviceDetails = null;

	private get actionOptions(): { title: string; settingsMeta: string }[] {
		return [
			{ title: "Off", settingsMeta: "0" },
			{ title: "On", settingsMeta: "1" },
			{ title: "Pulse", settingsMeta: "2" }
		];
	}

	public async created(): Promise<void> {
		this.resetDevicesState();

		await this.fetchDevicesByType({
			groupId: this.currentAlarm.groupID,
			deviceTypeId: DeviceType.Relay,
			filter: null
		});

		if (this.isEditing) {
			this.setStateForEditing();
		}
	}

	private setStateForEditing(): void {
		this.originalSettingsMeta = this.currentAlarmAction.settingsMeta;
		this.originalObjectId = this.currentAlarmAction.objectID;
		this.selectedDevice = {
			deviceID: this.currentAlarmAction.objectID,
			title: this.currentAlarmAction.deviceTitle
		} as DeviceDetails;
	}

	private handleDeviceSelected(): void {
		this.currentAlarmAction.objectID = this.selectedDevice && this.selectedDevice.deviceID;
	}

	private handleDeviceSearch(searchTerm: string, loading: (boolean) => void): void {
		loading(true);
		this.debounceSearch(searchTerm, loading);
	}

	private debounceSearch = debounce(async (searchTerm: string, loading: (boolean) => void): Promise<void> => {
		await this.searchDevices(searchTerm, loading);
	}, debouncePeriod);

	private async searchDevices(searchTerm: string, loading: (boolean) => void): Promise<void> {
		await this.fetchDevicesByType({
			groupId: this.currentAlarm.groupID,
			deviceTypeId: DeviceType.Relay,
			filter: searchTerm
		});

		loading(false);
	}
}
